import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plug, KeyRound, ShieldOff, Copy, BookOpen, Link2, Activity } from "lucide-react";
import { ConnectGuide } from "@/components/integrations/ConnectGuide";
import { MappingsManager } from "@/components/integrations/MappingsManager";

const freshnessAge = (m) => {
  if (m == null) return null;
  if (m < 60) return `${Math.round(m)}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

export default function Integrations() {
  const qc = useQueryClient();
  const [keyModal, setKeyModal] = useState(null);
  const [docsOpen, setDocsOpen] = useState(false);
  const [guideFor, setGuideFor] = useState(null);
  const [mappingsFor, setMappingsFor] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["integrations"], queryFn: async () => (await api.get("/integrations")).data });

  const issue = useMutation({
    mutationFn: async (id) => (await api.post(`/integrations/${id}/credentials`)).data,
    onSuccess: (d) => { qc.invalidateQueries(); setKeyModal(d); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const revoke = useMutation({
    mutationFn: async (id) => api.post(`/integrations/${id}/revoke`),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Credentials revoked"); },
  });
  const testUsage = useMutation({
    mutationFn: async (id) => (await api.post(`/integrations/${id}/test-usage`)).data,
    onSuccess: (d) => { qc.invalidateQueries(); toast.success(d.message || "Sample usage sent"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;

  return (
    <div className="ns-fade">
      <PageHeader title="Integrations" subtitle="Secure product-to-Admin data feeds — none connected until configured" testId="integrations-header">
        <Button variant="outline" onClick={() => setDocsOpen(true)} className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" data-testid="view-api-docs">
          <BookOpen className="mr-1.5 h-4 w-4" /> Integration API Docs
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {data.map((i, idx) => (
          <div key={i.id} className="ns-rise flex flex-col rounded-xl border border-[#1E2D4A] bg-[#17233B]/70 p-5" style={{ animationDelay: `${idx * 40}ms` }} data-testid={`integration-card-${i.key}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-500/10"><Plug className="h-4 w-4 text-cyan-400" /></div>
                <span className="font-semibold text-slate-100">{i.name}</span>
              </div>
              <StatusBadge status={i.derived_status} label={i.health_label} testId={`integration-status-${i.key}`} />
            </div>

            {i.future ? (
              <p className="mt-4 text-sm text-slate-500">Integration not configured. This connector will be available in a later release.</p>
            ) : (
              <>
                <div className="mt-4 space-y-1.5 text-xs" data-testid={`integration-freshness-${i.key}`}>
                  <Row l="Environment" v={<span className="capitalize">{i.environment}</span>} />
                  <Row l="Health" v={<span className="capitalize">{i.health_label}{i.age_minutes != null ? ` · ${freshnessAge(i.age_minutes)}` : ""}</span>} />
                  <Row l="Last request" v={i.last_request_at ? fmtDateTime(i.last_request_at) : "Never"} />
                  <Row l="Last success" v={i.last_success_at ? fmtDateTime(i.last_success_at) : "—"} />
                  <Row l="Active mappings" v={i.mapping_count ?? 0} />
                  {i.stale && <Row l="Freshness" v={<span className="text-orange-400">Stale — no data within threshold</span>} />}
                  {i.last_error && <Row l="Last error" v={<span className="text-rose-400">{i.last_error}</span>} />}
                </div>

                <div className="mt-4 flex flex-wrap gap-2 border-t border-[#1E2D4A] pt-3">
                  {i.credentials_active ? (
                    <>
                      <Button size="sm" variant="outline" className="flex-1 border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#1E2D4A]" onClick={() => issue.mutate(i.id)} data-testid={`rotate-key-${i.key}`}>
                        <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Rotate Key
                      </Button>
                      <Button size="sm" variant="outline" className="border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10" onClick={() => revoke.mutate(i.id)} data-testid={`revoke-key-${i.key}`}>
                        <ShieldOff className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" className="flex-1 bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" onClick={() => issue.mutate(i.id)} data-testid={`generate-key-${i.key}`}>
                      <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Generate API Key
                    </Button>
                  )}
                </div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="ghost" className="flex-1 text-cyan-300 hover:bg-cyan-500/10" onClick={() => setGuideFor(i)} data-testid={`connect-guide-${i.key}`}>
                    <BookOpen className="mr-1.5 h-3.5 w-3.5" /> Connect Guide
                  </Button>
                  <Button size="sm" variant="ghost" className="flex-1 text-slate-300 hover:bg-[#1E2D4A]" onClick={() => setMappingsFor(i)} data-testid={`mappings-${i.key}`}>
                    <Link2 className="mr-1.5 h-3.5 w-3.5" /> Mappings
                  </Button>
                </div>
                {i.credentials_active && (
                  <Button size="sm" variant="ghost" className="mt-1 w-full text-emerald-300 hover:bg-emerald-500/10" onClick={() => testUsage.mutate(i.id)} disabled={testUsage.isPending} data-testid={`test-usage-${i.key}`}>
                    <Activity className="mr-1.5 h-3.5 w-3.5" /> Send test usage
                  </Button>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Key reveal modal */}
      <Dialog open={!!keyModal} onOpenChange={(o) => !o && setKeyModal(null)}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100" data-testid="api-key-modal">
          <DialogHeader><DialogTitle>API Key Issued</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">Store this securely — it is shown only once. Use it server-to-server with header <code className="text-cyan-300">X-Integration-Key</code>.</p>
          <div className="space-y-2 rounded-lg border border-[#2D3F62] bg-[#0B1120] p-3">
            <div className="text-[11px] uppercase text-slate-500">Integration ID (X-Integration-Id)</div>
            <div className="font-mono-fin break-all text-xs text-slate-200">{keyModal?.integration_id}</div>
            <div className="mt-2 text-[11px] uppercase text-slate-500">API Key (X-Integration-Key)</div>
            <div className="flex items-center gap-2">
              <div className="font-mono-fin break-all text-xs text-cyan-300">{keyModal?.api_key}</div>
              <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-slate-400" onClick={() => { navigator.clipboard.writeText(keyModal?.api_key); toast.success("Copied"); }} data-testid="copy-api-key"><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Docs modal */}
      <Dialog open={docsOpen} onOpenChange={setDocsOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-[#1E2D4A] bg-[#111A2E] text-slate-100" data-testid="api-docs-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BookOpen className="h-4 w-4 text-cyan-400" /> NavScora Product Integration API — v1</DialogTitle></DialogHeader>
          <ApiDocs />
        </DialogContent>
      </Dialog>

      <ConnectGuide open={!!guideFor} onOpenChange={(o) => !o && setGuideFor(null)} integration={guideFor} />
      <MappingsManager open={!!mappingsFor} onOpenChange={(o) => !o && setMappingsFor(null)} integration={mappingsFor} />
    </div>
  );
}

const Row = ({ l, v }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500">{l}</span>
    <span className="text-slate-300">{v}</span>
  </div>
);

function ApiDocs() {
  const { data } = useQuery({ queryKey: ["api-docs"], queryFn: async () => (await api.get("/integrations/v1/docs")).data });
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-cyan-400" />;
  return (
    <div className="space-y-4 text-sm text-slate-300">
      <p className="text-slate-400">{data.data_minimisation}</p>
      <div>
        <SectionTitle className="mb-2">Authentication</SectionTitle>
        <div className="rounded-lg border border-[#1E2D4A] bg-[#0B1120] p-3 text-xs">
          <div>Scheme: {data.authentication.scheme}</div>
          <div className="mt-1 font-mono-fin text-cyan-300">X-Integration-Id: &lt;uuid&gt;</div>
          <div className="font-mono-fin text-cyan-300">X-Integration-Key: nsk_...</div>
          <div className="mt-1 text-slate-500">{data.authentication.transport}</div>
        </div>
      </div>
      <div>
        <SectionTitle className="mb-2">Customer mapping</SectionTitle>
        <p className="text-xs text-slate-400">{data.customer_mapping?.rule}</p>
        <p className="mt-1 font-mono-fin text-[11px] text-cyan-200">{data.customer_mapping?.resolution}</p>
        <p className="mt-1 text-xs text-amber-300/80">{data.customer_mapping?.production}</p>
      </div>
      <div>
        <SectionTitle className="mb-2">Request protection</SectionTitle>
        <div className="grid grid-cols-1 gap-1 rounded-lg border border-[#1E2D4A] bg-[#0B1120] p-3 text-[11px] text-slate-400 sm:grid-cols-2">
          <div>Rate limit: {data.protections?.rate_limit_per_minute}/min per integration</div>
          <div>Max body: {Math.round((data.protections?.max_request_bytes || 0) / 1024)} KB</div>
          <div>Timestamp: {data.protections?.timestamp_header}</div>
          <div>Replay: {data.protections?.replay_protection}</div>
          <div>Audit: {data.protections?.audit_logging}</div>
          <div>Revoked keys: {data.protections?.revoked_keys}</div>
        </div>
      </div>
      <div>
        <SectionTitle className="mb-2">Health states</SectionTitle>
        <div className="flex flex-wrap gap-1.5">{(data.health_states || []).map((s) => <StatusBadge key={s} status={s} />)}</div>
      </div>
      <div>
        <SectionTitle className="mb-2">Allowed event types</SectionTitle>
        <div className="flex flex-wrap gap-1.5 text-[11px]">
          {(data.allowed_event_types || []).map((t) => <span key={t} className="rounded border border-[#1E2D4A] bg-[#0B1120] px-2 py-0.5 font-mono-fin text-cyan-200">{t}</span>)}
        </div>
      </div>
      <div>
        <SectionTitle className="mb-2">Idempotency</SectionTitle>
        <p className="text-xs text-slate-400">{data.idempotency}</p>
      </div>
      <div>
        <SectionTitle className="mb-2">Endpoints ({data.base_url})</SectionTitle>
        <div className="space-y-2">
          {data.endpoints.map((e, i) => (
            <div key={i} className="rounded-lg border border-[#1E2D4A] bg-[#17233B]/50 p-3 text-xs">
              <div className="font-mono-fin"><span className="text-emerald-400">{e.method}</span> <span className="text-slate-200">{data.base_url}{e.path}</span></div>
              {e.required && <div className="mt-1 text-slate-500">Required: {e.required.join(", ")}</div>}
              {e.optional && <div className="text-slate-500">Optional: {e.optional.join(", ")}</div>}
              {e.metrics_example && <pre className="mt-2 overflow-x-auto rounded bg-[#0B1120] p-2 text-[11px] text-cyan-200">{JSON.stringify(e.metrics_example, null, 2)}</pre>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
